import { localeNamespace } from '@internal/utils/machine/i18n/Locale';
import { Chevrons } from '@renderer-ui-library/atoms';
import {
    ProductCard,
    VisibilityChangeWrapper,
} from '@renderer-ui-library/molecules';
import { Grid, Typography } from '@renderer-ui-library/mui';
import { BlockWrapper } from '@renderer-ui-library/scaffolding/BlockWrapper/BlockWrapper';
import { BlockProps } from 'blocks/BlockProps';
import { translations } from 'i18n/translations';
import { useTranslation } from 'next-i18next';
import React, { useCallback } from 'react';
import { useMachineTypeName } from 'utils/machine/useMachineTypeName';
import { useMachineUrl } from 'utils/routing/hooks/useMachineUrl';
import { tracker } from 'utils/tracking/tracker';
import { ISimilarMachinesBlockData } from './ISimilarMachinesBlockData';
import { TServerSideSimilarMachinesBlockProps } from './TServerSideSimilarMachinesBlockProps';
import styles from './similarMachinesBlock.module.scss';

export const SimilarMachinesBlock: React.FC<
    BlockProps<ISimilarMachinesBlockData, TServerSideSimilarMachinesBlockProps>
> = React.memo(({ locale, preFetchedBlockProps, data }) => {
    const { t } = useTranslation(localeNamespace.common);
    const { getMachineUrl } = useMachineUrl();
    const { getMachineTypeName } = useMachineTypeName();
    const handleSimilarMachineClick = useCallback(
        () =>
            tracker.trackEvent({
                name: 'click',
                target_name: 'similar_machines_block.machine',
                is_interaction: true,
            }),
        [],
    );

    const machines = preFetchedBlockProps.machines;
    const machineType = machines.length && machines[0]?.machineType;

    const handleScrolledIntoViewportOnce = useCallback(() => {
        tracker.trackEvent({
            name: 'ProductImpressions',
            action: 'listing-tag',
            ecommerce: {
                impressions: machines.map(m => ({
                    id: m.machineGroupId.toLowerCase(),
                    category: m.machineType,
                })),
            },
        });
    }, [machines]);

    if (!machineType || machines.length === 0) {
        return null;
    }

    return (
        <BlockWrapper blockData={data}>
            <VisibilityChangeWrapper
                onScrolledIntoViewportOnce={handleScrolledIntoViewportOnce}
            >
                <div className={styles.title}>
                    <Chevrons />
                    <Typography variant="h2">
                        {t(translations.machineDetailsSimilarMachineTitle, {
                            value: getMachineTypeName(
                                machineType,
                                machines.length === 1 ? 'singular' : 'plural',
                            ),
                            locale,
                        })}
                    </Typography>
                </div>
                <Grid container columnSpacing={2} rowSpacing={{ xs: 3, lg: 4 }}>
                    {machines.map(machine => (
                        <Grid
                            key={machine.machineGroupId}
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            lg={3}
                            onClick={handleSimilarMachineClick}
                        >
                            <ProductCard machine={machine} />
                        </Grid>
                    ))}
                </Grid>
            </VisibilityChangeWrapper>
        </BlockWrapper>
    );
});

SimilarMachinesBlock.displayName = 'SimilarMachinesBlock';
